import PageViewLayout from '@/components/layouts/PageViewLayout/PageViewLayout';
import TopTabBar from '@/components/modules/navigation/TopTabBar/TopTabBar';
import AppointmentsTab from '@/components/modules/pages/bokningar/AppointmentsTab';
import SEO, { seoPropsFromBaseString } from '@/components/modules/SEO';
import EmptyState from '@/components/templates/EmptyState';
import { APP_ROUTES } from '@/constants/pages';
import AppDownloadPrompt from '@/features/app-download-prompt/AppDownloadPrompt';
import { isLanding, isServer } from '@/helpers';
import { useAppSelector } from '@/hooks';
import { LoginContext } from '@/hooks/useLogin';
import { _s, _t } from '@/locale';
import { LoginSignupFlowInitiatedFrom } from '@/types/analytics';
import { Fragment, useContext, useEffect } from 'react';
import { Route, Switch, useLocation, useRouteMatch } from 'react-router-dom';

const bookingsMenu = [
  {
    label: _s('upcomingShort'),
    to: '/bokningar',
  },
  {
    label: _s('pastShort'),
    to: '/bokningar/tidigare',
  },
];

const Appointments = () => {
  const { user } = useAppSelector((state) => state.users);
  const isWhitelabel = useAppSelector((state) => state?.settings?.isWhitelabel);
  const location = useLocation<{ fromNavigation?: boolean; fromFooter?: boolean }>();
  const loginContext = useContext(LoginContext);
  const isFinishedTab = Boolean(useRouteMatch(APP_ROUTES.MY_BOOKINGS_FINISHED));

  useEffect(() => {
    if (isServer) return;
    window.scrollTo(0, 0);
  }, []);

  const isLogged = Boolean(user && user.token);

  if (!isLogged) {
    const screenName = isFinishedTab ? 'my_bookings_finished' : 'my_bookings_upcoming';
    let initiatedFrom: LoginSignupFlowInitiatedFrom = 'direct_links';

    if (location.state?.fromNavigation) {
      initiatedFrom = 'navigation_bar_bookings';
    }

    if (location.state?.fromFooter) {
      initiatedFrom = 'footer_bookings';
    }

    return (
      <Fragment key="no-user">
        <PageViewLayout
          type="mainView"
          wrapperClass="bg-surface_mp min-h-viewport flex flex-col"
          footerDisplayOption={{ from: 'md', to: 'all' }}>
          {/* Takes the slack on short pages: footer at the viewport bottom, content centred above it */}
          <div className="flex-1 md:flex md:flex-col md:justify-center">
            {/* A salon's white-label site never sends its visitors to the Bokadirekt app. */}
            {isWhitelabel ? (
              <EmptyState
                title={_s('appointments.emptyState.title')}
                body={_s('appointments.emptyState.body')}
                icon={<img width={354} height={237} src="/images/illustrations/bookings2.png" alt="" />}
                cta={loginContext.renderLoginButton({
                  label: _t('appointments.emptyState.button'),
                  size: 'sm',
                  trackPromptEvent: true,
                  initiatedFrom,
                })}
              />
            ) : (
              <AppDownloadPrompt screenName={screenName} />
            )}
          </div>
          <SEO {...seoPropsFromBaseString('appointments', '/bokningar')} image={'/images/home-background.jpeg'} />
        </PageViewLayout>
      </Fragment>
    );
  }

  const headerSlot = (
    <div className="mx-auto max-w-md">
      <TopTabBar
        items={bookingsMenu}
        defaultSelected={bookingsMenu.findIndex((item) => item.to === location.pathname)}
      />
    </div>
  );

  return (
    <Fragment key="user">
      <PageViewLayout
        type="mainView"
        headerSlot={headerSlot}
        bottomSlot={headerSlot}
        footerDisplayOption={{ from: 'md', to: 'all' }}
        wrapperClass="bg-gradient md:min-h-viewport md:flex md:flex-col"
        {...(isLanding() && { source: 'landing' })}>
        <div className="pt-lg container max-w-screen-md flex-1">
          <Switch>
            <Route
              exact
              path={APP_ROUTES.MY_BOOKINGS}
              children={<AppointmentsTab key="upcoming" filter="uFilterActive" />}
            />
            <Route
              path={APP_ROUTES.MY_BOOKINGS_FINISHED}
              children={<AppointmentsTab key="past" past filter="uFilterCanceled" />}
            />
          </Switch>
        </div>
        <SEO {...seoPropsFromBaseString('appointments', '/bokningar')} image={'/images/home-background.jpeg'} />
      </PageViewLayout>
    </Fragment>
  );
};

export default Appointments;
